import { HttpClient, HttpContext, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { SKIP_ERROR_TOAST } from 'src/app/shared/http-context-tokens';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { BaseServices } from '../../kuba.services';
import {
  AddActionPointRequest,
  FlexiCompliance,
  FlexiComplianceActionPoint,
  FlexiComplianceActionPointEvent,
  FlexiComplianceActionPointListItem,
  FlexiComplianceActionPointOverview,
  FlexiComplianceActionPointPage,
  FlexiComplianceActionPointPageQuery,
  FlexiComplianceActionPointStatus,
  FlexiComplianceHistoryItem,
  FlexiComplianceLineItem,
  FlexiComplianceRating,
  FlexiComplianceRecurrencePreview,
  FlexiComplianceRecurrenceType,
  FlexiComplianceResponsibleParty,
  FlexiComplianceScorePoint,
  FlexiComplianceStatus,
  FlexiComplianceSummary,
  isFlexiComplianceSnapshot,
  NewComplianceCustomItemRequest,
  RateComplianceItemRequest,
  UpdateActionPointRequest,
} from './flexi-compliance.models';

/**
 * What the route resolver puts on the snapshot. The container component
 * uses `role` to pick which UI to render:
 *  - 'list'     → history grid of reviews. What a CSM gets on the bare tab
 *                  route; the grid fetches its own rows.
 *  - 'csm'      → CSM authoring view (rating component) for the live review.
 *  - 'archived' → an archived or superseded review, opened read-only from
 *                  the grid.
 *  - 'customer' → customer read-only view + score.
 *  - 'locked'   → nothing started yet OR no completed assessment to show.
 */
export interface FlexiComplianceRouteData {
  role: 'list' | 'csm' | 'archived' | 'customer' | 'locked';
  aggregate?: FlexiCompliance;
}

/**
 * Numeric fields that stay null when the server sends null. `Number(null)` is
 * 0, which would turn "never completed" into a real score of zero.
 */
function toNullableNumber(value: any): number | null {
  return value === null || value === undefined ? null : Number(value);
}

@Injectable({ providedIn: 'root' })
export class FlexiComplianceService {
  private readonly base = `${environment.BASE_URL}/flexi/compliance`;

  /**
   * Emits whenever the AP list could have changed (add / update / delete).
   * Parent <c>flexi.component.ts</c> subscribes so it can re-probe and
   * append the Action Points tab the moment the first AP lands — without
   * this signal, the tab stays hidden until a manual page refresh.
   */
  private readonly actionPointsChanged$ = new Subject<void>();
  readonly actionPointsChanges = this.actionPointsChanged$.asObservable();

  constructor(private http: HttpClient) {}

  // -------- CSM authoring --------

  getOrCreate(businessId: number): Observable<FlexiCompliance> {
    return this.http.get<any>(`${this.base}/${businessId}`).pipe(map(r => this.mapAggregate(r)));
  }

  rateItem(businessId: number, lineItemId: number, rating: FlexiComplianceRating | null, comment: string | null): Observable<FlexiComplianceLineItem> {
    const body: RateComplianceItemRequest = { rating, comment };
    return this.http.put<any>(`${this.base}/${businessId}/items/${lineItemId}`, body)
      .pipe(map(r => this.mapLineItem(r)));
  }

  addCustomItem(businessId: number, sectionId: number, body: NewComplianceCustomItemRequest): Observable<FlexiCompliance> {
    return this.http.post<any>(`${this.base}/${businessId}/sections/${sectionId}/items`, body)
      .pipe(map(r => this.mapAggregate(r)));
  }

  removeItem(businessId: number, lineItemId: number): Observable<FlexiCompliance> {
    return this.http.delete<any>(`${this.base}/${businessId}/items/${lineItemId}`)
      .pipe(map(r => this.mapAggregate(r)));
  }

  restoreItem(businessId: number, lineItemId: number): Observable<FlexiCompliance> {
    return this.http.post<any>(`${this.base}/${businessId}/items/${lineItemId}/restore`, {})
      .pipe(map(r => this.mapAggregate(r)));
  }

  complete(businessId: number): Observable<FlexiCompliance> {
    return this.http.post<any>(`${this.base}/${businessId}/complete`, {})
      .pipe(map(r => this.mapAggregate(r)));
  }

  reopen(businessId: number): Observable<FlexiCompliance> {
    return this.http.post<any>(`${this.base}/${businessId}/reopen`, {})
      .pipe(map(r => this.mapAggregate(r)));
  }

  /**
   * Archive the current assessment (kept as audit history with its PDF +
   * APs) and seed a fresh one from the chosen template. Use this when
   * editing the template after completion would otherwise silently diverge
   * from the snapshot.
   */
  startNewReview(businessId: number, templateId: number): Observable<FlexiCompliance> {
    return this.http.post<any>(
      `${this.base}/${businessId}/new-review?templateId=${templateId}`,
      {},
    ).pipe(map(r => this.mapAggregate(r)));
  }

  /**
   * How many action points starting a new review would write off as rejected.
   *
   * Read before the confirmation, not after: the sweep is irreversible from the
   * UI, so the number belongs in the question rather than in the result. The
   * server counts it with the same query the sweep selects on, so the warning
   * and the outcome cannot disagree.
   */
  countOutstandingActionPoints(businessId: number): Observable<number> {
    return this.http
      .get<any>(`${this.base}/${businessId}/outstanding-action-points`)
      .pipe(map(r => r?.count ?? r?.Count ?? 0));
  }

  /**
   * Every review for the business, newest first — the history grid's rows.
   * Includes the live review as well as archived ones, so a review that has
   * never been completed still appears (with a null score).
   */
  listReviews(businessId: number): Observable<FlexiComplianceSummary[]> {
    return this.http
      .get<any[]>(`${this.base}/${businessId}/reviews`)
      .pipe(map(arr => (arr || []).map(r => this.mapSummary(r))));
  }

  /**
   * Soft-deletes a whole review. The row stays on disk for the audit trail
   * but leaves the review list, and the cascade takes its action points with
   * it — completed ones included, which the per-action-point delete refuses
   * to remove. Score snapshots stay on the benchmark chart. CSM-only.
   */
  deleteReview(businessId: number, complianceId: number): Observable<void> {
    return this.http
      .delete<void>(`${this.base}/${businessId}/reviews/${complianceId}`)
      // The Action Points tab lists across reviews, so it has to re-probe.
      .pipe(tap(() => this.actionPointsChanged$.next()));
  }

  /** Archives a review without deleting its action points or benchmark history. */
  archiveReview(businessId: number, complianceId: number): Observable<void> {
    return this.http
      .post<void>(`${this.base}/${businessId}/reviews/${complianceId}/archive`, {})
      .pipe(tap(() => this.actionPointsChanged$.next()));
  }

  /** Restores an archived review as the active review. */
  restoreReview(businessId: number, complianceId: number): Observable<void> {
    return this.http
      .post<void>(`${this.base}/${businessId}/reviews/${complianceId}/restore`, {})
      .pipe(tap(() => this.actionPointsChanged$.next()));
  }

  /** Loads one review for viewing — used to open an archived one read-only. */
  getReview(businessId: number, complianceId: number): Observable<FlexiCompliance> {
    return this.http
      .get<any>(`${this.base}/${businessId}/reviews/${complianceId}`)
      .pipe(map(r => this.mapAggregate(r)));
  }

  /**
   * Up to `limit` previously-completed assessments for the business with
   * persisted final scores. Drives the "Previous reviews" strip.
   */
  history(businessId: number, limit = 10): Observable<FlexiComplianceHistoryItem[]> {
    return this.http
      .get<any[]>(`${this.base}/${businessId}/history?limit=${limit}`)
      .pipe(map(arr => (arr || []).map(r => this.mapHistoryItem(r))));
  }

  /**
   * Every score the business has COMPLETED, oldest first — the Score-referanse
   * chart's series.
   *
   * Not `listReviews`, which the chart used to derive its points from: that is
   * one row per review carrying its latest score only, so re-completing a
   * review overwrote its own point. This endpoint reads the append-only
   * completion log, so a repeated assessment on the same template adds a point
   * and the progression stays visible.
   */
  scoreTimeline(businessId: number): Observable<FlexiComplianceScorePoint[]> {
    return this.http
      .get<any[]>(`${this.base}/${businessId}/score-timeline`)
      .pipe(map(arr => (arr || []).map(r => this.mapScorePoint(r))));
  }

  private mapSummary = (r: any): FlexiComplianceSummary => ({
    id: r.id ?? r.Id,
    businessId: r.businessId ?? r.BusinessId,
    status: (r.status ?? r.Status ?? 0) as FlexiComplianceStatus,
    isCurrent: r.isCurrent ?? r.IsCurrent ?? false,
    createdOn: r.createdOn ?? r.CreatedOn,
    periodTo: r.periodTo ?? r.PeriodTo ?? null,
    createdByName: r.createdByName ?? r.CreatedByName ?? null,
    modifiedByName: r.modifiedByName ?? r.ModifiedByName ?? null,
    modifiedOn: r.modifiedOn ?? r.ModifiedOn ?? null,
    completedOn: r.completedOn ?? r.CompletedOn ?? null,
    completedByName: r.completedByName ?? r.CompletedByName ?? null,
    catalogVersionCode: r.catalogVersionCode ?? r.CatalogVersionCode ?? '',
    templateId: r.templateId ?? r.TemplateId ?? null,
    templateName: r.templateName ?? r.TemplateName ?? null,
    // Null percentage means "never completed" and must survive as null —
    // the trend chart skips those rows rather than plotting them as zero.
    percentage: toNullableNumber(r.percentage ?? r.Percentage),
    smiley: (r.smiley ?? r.Smiley ?? null) as FlexiComplianceSummary['smiley'],
    greenCount: toNullableNumber(r.greenCount ?? r.GreenCount),
    yellowCount: toNullableNumber(r.yellowCount ?? r.YellowCount),
    redCount: toNullableNumber(r.redCount ?? r.RedCount),
    ratedCount: toNullableNumber(r.ratedCount ?? r.RatedCount),
    totalCount: toNullableNumber(r.totalCount ?? r.TotalCount),
    generatedFeatureManualDocumentId:
      r.generatedFeatureManualDocumentId ?? r.GeneratedFeatureManualDocumentId ?? null,
  });

  private mapScorePoint = (r: any): FlexiComplianceScorePoint => ({
    id: r.id ?? r.Id,
    reviewId: r.reviewId ?? r.ReviewId,
    sequence: r.sequence ?? r.Sequence ?? 1,
    isRepeat: r.isRepeat ?? r.IsRepeat ?? false,
    completedOn: r.completedOn ?? r.CompletedOn,
    completedByName: r.completedByName ?? r.CompletedByName ?? null,
    catalogVersionCode: r.catalogVersionCode ?? r.CatalogVersionCode ?? '',
    templateId: r.templateId ?? r.TemplateId ?? null,
    templateName: r.templateName ?? r.TemplateName ?? null,
    isCurrentReview: r.isCurrentReview ?? r.IsCurrentReview ?? false,
    // A completion always has a score, so 0 here means a genuine 0 % — unlike
    // mapSummary, which keeps null to mean "never completed".
    percentage: Number(r.percentage ?? r.Percentage ?? 0),
    smiley: (r.smiley ?? r.Smiley ?? 'red') as 'red' | 'yellow' | 'green',
    greenCount: r.greenCount ?? r.GreenCount ?? 0,
    yellowCount: r.yellowCount ?? r.YellowCount ?? 0,
    redCount: r.redCount ?? r.RedCount ?? 0,
    ratedCount: r.ratedCount ?? r.RatedCount ?? 0,
    totalCount: r.totalCount ?? r.TotalCount ?? 0,
    generatedFeatureManualDocumentId:
      r.generatedFeatureManualDocumentId ?? r.GeneratedFeatureManualDocumentId ?? null,
  });

  private mapHistoryItem = (r: any): FlexiComplianceHistoryItem => ({
    id: r.id ?? r.Id,
    completedOn: r.completedOn ?? r.CompletedOn ?? null,
    completedByName: r.completedByName ?? r.CompletedByName ?? null,
    catalogVersionCode: r.catalogVersionCode ?? r.CatalogVersionCode ?? '',
    templateId: r.templateId ?? r.TemplateId ?? null,
    templateName: r.templateName ?? r.TemplateName ?? null,
    isCurrent: r.isCurrent ?? r.IsCurrent ?? false,
    percentage: Number(r.percentage ?? r.Percentage ?? 0),
    smiley: (r.smiley ?? r.Smiley ?? 'red') as 'red' | 'yellow' | 'green',
    greenCount: r.greenCount ?? r.GreenCount ?? 0,
    yellowCount: r.yellowCount ?? r.YellowCount ?? 0,
    redCount: r.redCount ?? r.RedCount ?? 0,
    ratedCount: r.ratedCount ?? r.RatedCount ?? 0,
    totalCount: r.totalCount ?? r.TotalCount ?? 0,
    generatedFeatureManualDocumentId: r.generatedFeatureManualDocumentId ?? r.GeneratedFeatureManualDocumentId ?? null,
  });

  // -------- Action points --------

  addActionPoint(businessId: number, lineItemId: number, body: AddActionPointRequest): Observable<FlexiComplianceActionPoint> {
    return this.http.post<any>(`${this.base}/${businessId}/items/${lineItemId}/action-points`, body)
      .pipe(map(r => this.mapActionPoint(r)),
            tap(() => this.actionPointsChanged$.next()));
  }

  /**
   * Adds a "loose" action point — one that belongs to the business's
   * compliance assessment but isn't pinned to a specific line item. Lives
   * in the same `FlexiComplianceActionPoint` table; the FE distinguishes by
   * <c>flexiComplianceLineItemId === null</c>.
   */
  addLooseActionPoint(businessId: number, body: AddActionPointRequest): Observable<FlexiComplianceActionPoint> {
    return this.http.post<any>(`${this.base}/${businessId}/action-points`, body)
      .pipe(map(r => this.mapActionPoint(r)),
            tap(() => this.actionPointsChanged$.next()));
  }

  /**
   * Lists every non-deleted action point on the business's compliance
   * assessment, enriched with source context (section + originating
   * question for attached APs; null for loose ones). Drives the
   * Flexi → Action Points tab.
   */
  listActionPoints(businessId: number): Observable<FlexiComplianceActionPointListItem[]> {
    return this.http.get<any[]>(`${this.base}/${businessId}/action-points`)
      .pipe(map(arr => (arr || []).map(r => this.mapActionPointListItem(r))));
  }

  updateActionPoint(businessId: number, actionPointId: number, body: UpdateActionPointRequest): Observable<FlexiComplianceActionPoint> {
    return this.http.put<any>(`${this.base}/${businessId}/action-points/${actionPointId}`, body)
      .pipe(map(r => this.mapActionPoint(r)),
            tap(() => this.actionPointsChanged$.next()));
  }

  /**
   * Both candidate deadlines for completing a recurring action point today.
   * Called before the completion so the confirmation dialog can label its
   * buttons with real dates — the arithmetic stays on the server, so the date
   * shown is the date that will be written.
   */
  previewNextDeadline(businessId: number, actionPointId: number): Observable<FlexiComplianceRecurrencePreview> {
    return this.http
      .get<any>(`${this.base}/${businessId}/action-points/${actionPointId}/next-deadline`)
      .pipe(map(r => this.mapRecurrencePreview(r)));
  }

  /** Every instance of one recurring series, oldest cycle first. */
  actionPointSeries(businessId: number, seriesId: number): Observable<FlexiComplianceActionPointListItem[]> {
    return this.http
      .get<any[]>(`${this.base}/${businessId}/action-points/series/${seriesId}`)
      .pipe(map(arr => (arr || []).map(r => this.mapActionPointListItem(r))));
  }

  /**
   * One page of action points for the grid, 10 per page by default.
   *
   * The filters travel to the server with the page because filtering ten rows
   * in the browser would narrow the answer to the page in hand. Whole-list
   * tallies come back in `metadata` so the filter chips can describe every
   * action point rather than the page.
   */
  listActionPointsPaged(
    businessId: number,
    query: FlexiComplianceActionPointPageQuery,
  ): Observable<FlexiComplianceActionPointPage> {
    let params = new HttpParams()
      .set('pageNumber', String(query.pageNumber))
      .set('pageSize', String(query.pageSize));

    // Omitted rather than sent as null: the BE reads "absent" as "no filter",
    // and a literal "null" in the query string would fail model binding.
    if (query.isRecurring === true || query.isRecurring === false) {
      params = params.set('isRecurring', String(query.isRecurring));
    }
    for (const status of query.statuses ?? []) {
      params = params.append('statuses', String(status));
    }
    for (const rating of query.sourceRatings ?? []) {
      params = params.append('sourceRatings', String(rating));
    }
    if (query.recurrenceType !== null && query.recurrenceType !== undefined) {
      params = params.set('recurrenceType', String(query.recurrenceType));
    }
    if (query.focusActionPointId) {
      params = params.set('focusActionPointId', String(query.focusActionPointId));
    }

    return this.http
      .get<any>(`${this.base}/${businessId}/action-points/paged`, { params })
      .pipe(map(r => this.mapActionPointPage(r, query)));
  }

  private mapActionPointPage = (
    r: any,
    query: FlexiComplianceActionPointPageQuery,
  ): FlexiComplianceActionPointPage => {
    const rawItems: any[] = r?.items ?? r?.Items ?? [];
    const rawMeta: any = r?.metadata ?? r?.Metadata ?? {};
    return {
      items: rawItems.map(i => this.mapActionPointListItem(i)),
      // Echoed by the server, which is the only side that knows the page when
      // focusActionPointId was used. Falls back to what we asked for.
      pageNumber: r?.pageNumber ?? r?.PageNumber ?? query.pageNumber,
      pageSize: r?.pageSize ?? r?.PageSize ?? query.pageSize,
      totalCount: r?.totalCount ?? r?.TotalCount ?? 0,
      totalPages: r?.totalPages ?? r?.TotalPages ?? 0,
      metadata: {
        allCount: rawMeta.allCount ?? rawMeta.AllCount ?? 0,
        recurringCount: rawMeta.recurringCount ?? rawMeta.RecurringCount ?? 0,
        oneOffCount: rawMeta.oneOffCount ?? rawMeta.OneOffCount ?? 0,
        openCount: rawMeta.openCount ?? rawMeta.OpenCount ?? 0,
        inProgressCount: rawMeta.inProgressCount ?? rawMeta.InProgressCount ?? 0,
        completedCount: rawMeta.completedCount ?? rawMeta.CompletedCount ?? 0,
        rejectedCount: rawMeta.rejectedCount ?? rawMeta.RejectedCount ?? 0,
        missingCount: rawMeta.missingCount ?? rawMeta.MissingCount ?? 0,
        needsImprovementCount: rawMeta.needsImprovementCount ?? rawMeta.NeedsImprovementCount ?? 0,
        skippedCycleCount: rawMeta.skippedCycleCount ?? rawMeta.SkippedCycleCount ?? 0,
      },
    };
  };

  /** Audit timeline for one action point, already localized server-side. */
  actionPointTimeline(businessId: number, actionPointId: number): Observable<FlexiComplianceActionPointEvent[]> {
    return this.http
      .get<any[]>(`${this.base}/${businessId}/action-points/${actionPointId}/timeline`)
      .pipe(map(arr => (arr || []).map(r => this.mapTimelineEvent(r))));
  }

  /**
   * Every outstanding action point, worst-first — skipped cycles, then most
   * overdue, then by deadline. Drives the Handlingsplan dashboard card.
   *
   * A business without an assessment, or a caller who may not read the action
   * points, is an empty card rather than a failure, so the generic error toast
   * is suppressed here as well (#6021).
   */
  actionPointOverview(businessId: number): Observable<FlexiComplianceActionPointOverview[]> {
    return this.http
      .get<any[]>(`${this.base}/${businessId}/action-points/overview`, {
        context: new HttpContext().set(SKIP_ERROR_TOAST, true)
      })
      .pipe(map(arr => (arr || []).map(r => this.mapActionPointOverview(r))));
  }

  /**
   * Whether this user may see the business's action points at all.
   *
   * Action points are gated to Editor / Portal / Admin, the assigned HSE
   * advisor, and the user named as the business's OHS responsible for action
   * points — every other role is refused by the BE. This probe asks that same
   * question as data so the Action Points tab and the Handlingsplan dashboard
   * card can be absent for those users rather than present and 403-ing.
   *
   * Any failure resolves to false: "we could not establish access" and "no
   * access" should both render nothing.
   */
  actionPointAccess(businessId: number): Observable<boolean> {
    return this.http
      .get<boolean>(`${this.base}/${businessId}/action-points/access`)
      .pipe(
        map(canView => !!canView),
        catchError(() => of(false)),
      );
  }

  private mapRecurrencePreview = (r: any): FlexiComplianceRecurrencePreview => ({
    actionPointId: r.actionPointId ?? r.ActionPointId,
    recurrenceType: (r.recurrenceType ?? r.RecurrenceType ?? 0) as FlexiComplianceRecurrenceType,
    recurrenceName: r.recurrenceName ?? r.RecurrenceName ?? '',
    currentDueDate: r.currentDueDate ?? r.CurrentDueDate ?? null,
    requiresScheduleChoice: r.requiresScheduleChoice ?? r.RequiresScheduleChoice ?? false,
    daysOverdue: r.daysOverdue ?? r.DaysOverdue ?? 0,
    originalScheduleNextDueDate: r.originalScheduleNextDueDate ?? r.OriginalScheduleNextDueDate,
    originalScheduleSkippedCycles: r.originalScheduleSkippedCycles ?? r.OriginalScheduleSkippedCycles ?? 0,
    shiftScheduleNextDueDate: r.shiftScheduleNextDueDate ?? r.ShiftScheduleNextDueDate,
  });

  private mapTimelineEvent = (r: any): FlexiComplianceActionPointEvent => ({
    id: r.id ?? r.Id,
    eventType: r.eventType ?? r.EventType ?? 0,
    message: r.message ?? r.Message ?? '',
    createdBy: r.createdBy ?? r.CreatedBy ?? null,
    createdByName: r.createdByName ?? r.CreatedByName ?? null,
    createdOn: r.createdOn ?? r.CreatedOn,
  });

  private mapActionPointOverview = (r: any): FlexiComplianceActionPointOverview => ({
    actionPointId: r.actionPointId ?? r.ActionPointId,
    seriesId: r.seriesId ?? r.SeriesId ?? null,
    title: r.title ?? r.Title ?? '',
    status: (r.status ?? r.Status ?? 0) as FlexiComplianceActionPointStatus,
    recurrenceType: (r.recurrenceType ?? r.RecurrenceType ?? 0) as FlexiComplianceRecurrenceType,
    recurrenceName: r.recurrenceName ?? r.RecurrenceName ?? '',
    dueDate: r.dueDate ?? r.DueDate ?? null,
    skippedCycles: r.skippedCycles ?? r.SkippedCycles ?? 0,
    scheduleShifted: r.scheduleShifted ?? r.ScheduleShifted ?? false,
    daysOverdue: r.daysOverdue ?? r.DaysOverdue ?? 0,
    isException: r.isException ?? r.IsException ?? false,
    // ?? rather than a falsy default: 0 is Customer, a real party, and would be
    // swallowed by ||.
    responsibleParty: (r.responsibleParty ?? r.ResponsibleParty ?? null) as FlexiComplianceResponsibleParty | null,
    responsibleUserName: r.responsibleUserName ?? r.ResponsibleUserName ?? null,
    hseAdvisorName: r.hseAdvisorName ?? r.HseAdvisorName ?? null,
  });

  deleteActionPoint(businessId: number, actionPointId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${businessId}/action-points/${actionPointId}`)
      .pipe(tap(() => this.actionPointsChanged$.next()));
  }

  // -------- Customer endpoint --------

  /**
   * The customer-facing compliance view. A business that has not started a
   * compliance review answers 404, which is a normal empty state rather than a
   * failure, so the generic error toast is suppressed and the callers render
   * their own empty / locked state instead (#6021).
   */
  getFinal(businessId: number): Observable<FlexiCompliance> {
    return this.http.get<any>(`${this.base}/${businessId}/final`, {
      context: new HttpContext().set(SKIP_ERROR_TOAST, true)
    })
      .pipe(map(r => this.mapAggregate(r)));
  }

  /**
   * Downloads the latest archived compliance report PDF blob. Same file
   * the OHS-plans folder serves, exposed here so the CSM "Download report"
   * button on the rating view and the customer button on the final view
   * fetch the same source of truth.
   */
  downloadReportPdf(businessId: number, complianceId?: number): Observable<Blob> {
    const query = complianceId ? `?complianceId=${complianceId}` : '';
    return this.http.get(
      `${this.base}/${businessId}/report.pdf${query}`, { responseType: 'blob' });
  }

  /**
   * Downloads the action points as a branded XLSX workbook.
   *
   * Server-side because the file carries an embedded logo — the business's own
   * if it has uploaded one, Kuba's otherwise — which the browser cannot
   * assemble the way it builds the CSV. The filters travel with the request so
   * the workbook matches what the grid is showing; paging does not, because the
   * file covers every matching row rather than the page in hand.
   */
  exportActionPointsXlsx(
    businessId: number,
    query: FlexiComplianceActionPointPageQuery,
  ): Observable<Blob> {
    let params = new HttpParams();
    if (query.isRecurring === true || query.isRecurring === false) {
      params = params.set('isRecurring', String(query.isRecurring));
    }
    for (const status of query.statuses ?? []) {
      params = params.append('statuses', String(status));
    }
    for (const rating of query.sourceRatings ?? []) {
      params = params.append('sourceRatings', String(rating));
    }
    if (query.recurrenceType !== null && query.recurrenceType !== undefined) {
      params = params.set('recurrenceType', String(query.recurrenceType));
    }
    return this.http.get(`${this.base}/${businessId}/action-points/export.xlsx`,
      { params, responseType: 'blob' });
  }

  /**
   * Re-runs the PDF generator on the BE. Returns the new
   * <c>FeatureManualDocument</c> id. CSM-only; the customer view never
   * calls this. Used to recover from a missing blob (Azure retention) or
   * refresh the archived copy after data changes elsewhere.
   */
  regenerateReportPdf(businessId: number): Observable<{ documentId: number }> {
    return this.http.post<any>(`${this.base}/${businessId}/regenerate-pdf`, {})
      .pipe(map(r => ({ documentId: r?.documentId ?? r?.DocumentId ?? 0 })));
  }

  // -------- Mappers: PascalCase → camelCase tolerant --------

  private mapAggregate = (r: any): FlexiCompliance => {
    if (!r) return r;
    return {
      id: r.id ?? r.Id,
      businessId: r.businessId ?? r.BusinessId,
      status: r.status ?? r.Status ?? 0,
      periodFrom: r.periodFrom ?? r.PeriodFrom ?? null,
      periodTo: r.periodTo ?? r.PeriodTo ?? null,
      assessedOn: r.assessedOn ?? r.AssessedOn ?? null,
      completedOn: r.completedOn ?? r.CompletedOn ?? null,
      assessedByUserId: r.assessedByUserId ?? r.AssessedByUserId ?? null,
      assessedByName: r.assessedByName ?? r.AssessedByName ?? null,
      catalogVersionCode: r.catalogVersionCode ?? r.CatalogVersionCode ?? '',
      catalogVersionName: r.catalogVersionName ?? r.CatalogVersionName ?? '',
      templateId: r.templateId ?? r.TemplateId ?? null,
      score: this.mapScore(r.score ?? r.Score),
      sections: ((r.sections ?? r.Sections) || []).map((s: any) => this.mapSection(s)),
    };
  };

  private mapSection = (s: any): any => ({
    id: s.id ?? s.Id,
    position: s.position ?? s.Position ?? 0,
    title: s.title ?? s.Title ?? '',
    translateKey: s.translateKey ?? s.TranslateKey ?? null,
    items: ((s.items ?? s.Items) || []).map((it: any) => this.mapLineItem(it)),
  });

  private mapLineItem = (r: any): FlexiComplianceLineItem => ({
    id: r.id ?? r.Id,
    position: r.position ?? r.Position ?? 0,
    sourceType: r.sourceType ?? r.SourceType ?? 0,
    question: r.question ?? r.Question ?? '',
    regulatoryReference: r.regulatoryReference ?? r.RegulatoryReference ?? null,
    weight: r.weight ?? r.Weight ?? 1,
    rating: r.rating ?? r.Rating ?? null,
    comment: r.comment ?? r.Comment ?? null,
    isRemoved: r.isRemoved ?? r.IsRemoved ?? false,
    actionPoints: ((r.actionPoints ?? r.ActionPoints) || []).map((a: any) => this.mapActionPoint(a)),
  });

  private mapActionPoint = (r: any): FlexiComplianceActionPoint => ({
    id: r.id ?? r.Id,
    flexiComplianceLineItemId: r.flexiComplianceLineItemId ?? r.FlexiComplianceLineItemId ?? null,
    title: r.title ?? r.Title ?? '',
    description: r.description ?? r.Description ?? null,
    responsibleParty: (r.responsibleParty ?? r.ResponsibleParty ?? null) as FlexiComplianceActionPoint['responsibleParty'],
    dueDate: r.dueDate ?? r.DueDate ?? null,
    status: (r.status ?? r.Status ?? 0) as FlexiComplianceActionPoint['status'],
    completedOn: r.completedOn ?? r.CompletedOn ?? null,
    completedByUserId: r.completedByUserId ?? r.CompletedByUserId ?? null,
    completedByName: r.completedByName ?? r.CompletedByName ?? null,
    createdBy: r.createdBy ?? r.CreatedBy ?? null,
    createdByName: r.createdByName ?? r.CreatedByName ?? null,
    createdOn: r.createdOn ?? r.CreatedOn,
    responsibleUserId: r.responsibleUserId ?? r.ResponsibleUserId ?? null,
    responsibleUserName: r.responsibleUserName ?? r.ResponsibleUserName ?? null,
    hseAdvisorUserId: r.hseAdvisorUserId ?? r.HseAdvisorUserId ?? null,
    hseAdvisorName: r.hseAdvisorName ?? r.HseAdvisorName ?? null,
    recurrenceType: (r.recurrenceType ?? r.RecurrenceType ?? 0) as FlexiComplianceRecurrenceType,
    recurrenceName: r.recurrenceName ?? r.RecurrenceName ?? null,
    seriesId: r.seriesId ?? r.SeriesId ?? null,
    parentActionPointId: r.parentActionPointId ?? r.ParentActionPointId ?? null,
    recurrenceCycleIndex: r.recurrenceCycleIndex ?? r.RecurrenceCycleIndex ?? 0,
    skippedCycles: r.skippedCycles ?? r.SkippedCycles ?? 0,
    scheduleShifted: r.scheduleShifted ?? r.ScheduleShifted ?? false,
  });

  private mapActionPointListItem = (r: any): FlexiComplianceActionPointListItem => ({
    ...this.mapActionPoint(r),
    sectionId: r.sectionId ?? r.SectionId ?? null,
    sectionTitle: r.sectionTitle ?? r.SectionTitle ?? null,
    sourceQuestion: r.sourceQuestion ?? r.SourceQuestion ?? null,
    sourceRating: r.sourceRating ?? r.SourceRating ?? null,
    flexiComplianceId: r.flexiComplianceId ?? r.FlexiComplianceId ?? 0,
    catalogVersionCode: r.catalogVersionCode ?? r.CatalogVersionCode ?? '',
    reviewCreatedOn: r.reviewCreatedOn ?? r.ReviewCreatedOn,
    reviewCompletedOn: r.reviewCompletedOn ?? r.ReviewCompletedOn ?? null,
    isCurrentReview: r.isCurrentReview ?? r.IsCurrentReview ?? false,
  });

  private mapScore = (s: any) => {
    const totalCount = s?.totalCount ?? s?.TotalCount ?? 0;
    const ratedCount = s?.ratedCount ?? s?.RatedCount ?? 0;
    const greenShare = Number(s?.greenShare ?? s?.GreenShare ?? 0);
    const yellowShare = Number(s?.yellowShare ?? s?.YellowShare ?? 0);
    const redShare = Number(s?.redShare ?? s?.RedShare ?? 0);
    return {
      percentage: s?.percentage ?? s?.Percentage ?? 0,
      smiley: (s?.smiley ?? s?.Smiley ?? 'red') as 'red' | 'yellow' | 'green',
      ratedCount,
      totalCount,
      greenCount: s?.greenCount ?? s?.GreenCount ?? 0,
      yellowCount: s?.yellowCount ?? s?.YellowCount ?? 0,
      redCount: s?.redCount ?? s?.RedCount ?? 0,
      // Derive rather than trust the wire for these two: an older BE build
      // (or the zeroed stub score the customer view sends before a review is
      // completed) omits them, and a silently-zero gray segment would make an
      // unassessed review look fully rated.
      unratedCount: s?.unratedCount ?? s?.UnratedCount ?? Math.max(0, totalCount - ratedCount),
      greenShare,
      yellowShare,
      redShare,
      unratedShare: Number(
        s?.unratedShare ?? s?.UnratedShare
        ?? Math.max(0, 100 - greenShare - yellowShare - redShare),
      ),
    };
  };
}

/**
 * Download name for a compliance report: the translated stem, the business it
 * belongs to, and the date of the report — `hms-status-Ørsta-Bygg-AS-09-09-2026.pdf`
 * in Norwegian, `ohs-compliance-Ørsta-Bygg-AS-09-09-2026.pdf` in English. The
 * business segment is dropped when there is no name to use.
 *
 * The stem is passed in already translated (from
 * FLEXI_COMPLIANCE.REPORT_FILE_NAME) because this file has no
 * TranslateService, and every call site has one. The date is formatted here
 * rather than with the `date` pipe: a download name is built in TypeScript,
 * and toLocaleDateString() would follow the browser's locale instead of the
 * dd-MM-yyyy the rest of the tab uses.
 */
export function complianceReportFileName(
  stem: string,
  businessName?: string | null,
  when?: string | Date | null,
): string {
  const parsed = when ? new Date(when) : new Date();
  const date = isNaN(parsed.getTime()) ? new Date() : parsed;
  const pad = (n: number) => String(n).padStart(2, '0');
  const day = `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
  const business = safeFileNameSegment(businessName);
  return business ? `${stem}-${business}-${day}.pdf` : `${stem}-${day}.pdf`;
}

/**
 * A business name reduced to something safe to hand a Save dialog: the
 * characters no filesystem accepts are dropped and whitespace becomes the same
 * dash the rest of the name uses. Deliberately keeps æ/ø/å and other letters —
 * the old per-view names stripped them, which turned "Ørsta Bygg" into "rsta
 * Bygg". Returns '' when there is nothing usable left, and the caller then
 * omits the segment rather than emitting a stray dash.
 */
function safeFileNameSegment(value?: string | null): string {
  return (value ?? '')
    .replace(/[\\\/:*?"<>|]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .trim();
}

/**
 * Whether the current user may AUTHOR assessments, i.e. gets the rating view
 * and the grid's write actions. Admin only — AbsoluteroleId stays '1' even in
 * R-mode, which is how an admin acts as the advisor. Portal is
 * editor-equivalent in Flexi and is not a CSM.
 *
 * The server is the authority (FlexiOnboardingConstant.CsmAuthorPolicy also
 * lets a business's assigned non-admin HSE advisor author); this is the
 * frontend's conservative approximation, used only to decide what to OFFER.
 * Anything it lets through is still checked server-side.
 */
export function isFlexiComplianceAuthor(): boolean {
  return String(BaseServices.AbsoluteroleId) === '1';
}

/**
 * Decides which view of the tab to render. Everyone — author and customer
 * alike — lands on the history grid; only opening a specific assessment
 * differs, and only an author gets the editable rating view. Mirrors
 * FlexiOnboardingResolver — the CSM endpoint is gated by
 * FlexiOnboardingCsmAuthor (Admin bypass or HseAdviserKey == currentUserId).
 * 403/404 → fall back to /final.
 */
@Injectable({ providedIn: 'root' })
export class FlexiComplianceResolver {
  constructor(private service: FlexiComplianceService) {}

  resolve(route: ActivatedRouteSnapshot): Observable<FlexiComplianceRouteData> {
    const businessId = BaseServices.BusinessId;

    // No `:id` means the tab itself was opened — show the history grid rather
    // than dropping anyone straight into a single assessment. Customers get it
    // too, read-only: the grid is where the score history and the
    // Score-referanse chart live, and they had no way to reach either before.
    // The grid fetches its own rows (the backend hides work in progress from
    // non-authors), so nothing needs resolving here.
    const complianceId = Number(route.paramMap.get('id'));
    if (!complianceId) {
      return of({ role: 'list' as const });
    }

    return this.service.getReview(businessId, complianceId).pipe(
      map(aggregate => ({
        // Only the live review is editable, and only by an author. An archived
        // or superseded one is an immutable snapshot, so it opens in the
        // read-only view even for a CSM — which is the same rendering a
        // customer gets.
        role: isFlexiComplianceAuthor() && !isFlexiComplianceSnapshot(aggregate.status)
          ? ('csm' as const)
          : ('archived' as const),
        aggregate,
      })),
      catchError((err: HttpErrorResponse) => {
        // 403 (not an author) and 404 (a draft the backend hides from
        // viewers) both mean "not yours to open" — fall back to the read-only
        // current-assessment view instead of an error page.
        if (err.status === 403 || err.status === 404) return this.loadCustomer(businessId);
        return throwError(() => err);
      }),
    );
  }

  private loadCustomer(businessId: number): Observable<FlexiComplianceRouteData> {
    return this.service.getFinal(businessId).pipe(
      map(aggregate => {
        // The BE returns Status + (potentially) empty Sections when the
        // assessment hasn't been Completed. Translate to "locked" so the
        // container hides the rating chrome.
        const hasContent = !!aggregate.sections && aggregate.sections.length > 0;
        return { role: hasContent ? 'customer' : 'locked', aggregate } as FlexiComplianceRouteData;
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 404) return of({ role: 'locked' as const });
        return throwError(() => err);
      }),
    );
  }

}
